import { useCallback, type RefObject } from 'react'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { useGetGlobalSearchResultsPresenter } from './useGetGlobalSearchResultsPresenter'
import { LlmConversationConfirmation } from '../../../../components/LlmConversationConfirmation'
import { GlobalSearchView } from '../../views/GlobalSearchView/GlobalSearchView'

interface IGetGlobalSearchResultsPresenterProps {
  onClose: () => void
  inputRef?: RefObject<HTMLInputElement>
  onHandoff?: (query: string) => void
}

export const GetGlobalSearchResultsPresenter = ({
  onClose,
  inputRef: externalInputRef,
  onHandoff,
}: IGetGlobalSearchResultsPresenterProps) => {
  const intl = useProcurifyIntl()
  const {
    commands: {
      changeQuery,
      openHighlightedOrSubmit,
      openRecentItem,
      openResultItem,
      selectTab,
      navigateDown,
      navigateUp,
    },
    data: {
      query,
      submittedQuery,
      recentItems,
      resultItems,
      tabs,
      inputRef,
      highlightedIndex,
      activeDescendantId,
      isExpanded,
      originalQuery,
    },
    states: {
      showEnterHint,
      contentState,
      selectedTab,
      showTruncatedNotice,
      noResultsMessage,
      unsupportedQueryMessage,
      errorMessage,
      showLlmConfirmation,
    },
  } = useGetGlobalSearchResultsPresenter(onClose, externalInputRef)

  const handleConfirmationAccept = useCallback(() => {
    onHandoff?.(originalQuery ?? submittedQuery)
  }, [onHandoff, originalQuery, submittedQuery])

  if (showLlmConfirmation && onHandoff) {
    return (
      <LlmConversationConfirmation
        query={query}
        inputRef={inputRef}
        title={intl.formatMessage(messages.confirmationTitle)}
        actionContent={intl.formatMessage(messages.confirmationAction)}
        actionAriaLabel={intl.formatMessage(messages.confirmationAction)}
        actionAnalyticsId='global-command-llm-confirmation-action'
        onChange={changeQuery}
        onEnter={openHighlightedOrSubmit}
        onActionClick={handleConfirmationAccept}
      />
    )
  }

  return (
    <GlobalSearchView
      query={query}
      showEnterHint={showEnterHint}
      contentState={contentState}
      recentItems={recentItems}
      resultItems={resultItems}
      tabs={tabs}
      selectedTab={selectedTab}
      showTruncatedNotice={showTruncatedNotice}
      noResultsMessage={noResultsMessage}
      unsupportedQueryMessage={unsupportedQueryMessage}
      errorMessage={errorMessage}
      inputRef={inputRef}
      highlightedIndex={highlightedIndex}
      activeDescendantId={activeDescendantId}
      isExpanded={isExpanded}
      onQueryChange={changeQuery}
      onEnter={openHighlightedOrSubmit}
      onNavigateDown={navigateDown}
      onNavigateUp={navigateUp}
      onRecentItemClick={openRecentItem}
      onResultItemClick={openResultItem}
      onTabChange={selectTab}
    />
  )
}
